import Chart from "chart.js/auto";

export default function initFinancialApp() {
  const TAX_BRACKETS = [
    { limit: 28000, rate: 0.23 },
    { limit: 50000, rate: 0.35 },
    { limit: Infinity, rate: 0.43 },
  ];

  const LOAN_COUNTRIES = {
    italy: { name: "Italia", maxDebtRatio: 0.33, maxYears: 30, avgRate: 4.2 },
    germany: { name: "Germania", maxDebtRatio: 0.35, maxYears: 30, avgRate: 3.8 },
    france: { name: "Francia", maxDebtRatio: 0.35, maxYears: 25, avgRate: 3.9 },
    spain: { name: "Spagna", maxDebtRatio: 0.35, maxYears: 30, avgRate: 4.0 },
  };

  const PENSION_SECTORS = {
    commercio: { name: "Commercio", employerContrib: 1.55, minEmployee: 0.55 },
    metalmeccanico: { name: "Metalmeccanico", employerContrib: 2.0, minEmployee: 0.5 },
    chimico: { name: "Chimico-Farmaceutico", employerContrib: 2.0, minEmployee: 1.0 },
    edilizia: { name: "Edilizia", employerContrib: 1.0, minEmployee: 1.0 },
    bancario: { name: "Bancario", employerContrib: 3.5, minEmployee: 0.5 },
    alimentare: { name: "Alimentare", employerContrib: 1.2, minEmployee: 1.0 },
    turismo: { name: "Turismo", employerContrib: 1.0, minEmployee: 1.0 },
    pubblico: { name: "Pubblico Impiego", employerContrib: 1.0, minEmployee: 0.5 },
  };

  const SALARY_REGIONS = {
    lombardia: { name: "Lombardia", addizionale: 1.73 },
    lazio: { name: "Lazio", addizionale: 3.33 },
    piemonte: { name: "Piemonte", addizionale: 1.62 },
    veneto: { name: "Veneto", addizionale: 1.23 },
    emilia: { name: "Emilia-Romagna", addizionale: 2.03 },
    toscana: { name: "Toscana", addizionale: 1.42 },
    campania: { name: "Campania", addizionale: 2.03 },
    sicilia: { name: "Sicilia", addizionale: 1.73 },
    puglia: { name: "Puglia", addizionale: 1.73 },
    calabria: { name: "Calabria", addizionale: 2.03 },
    liguria: { name: "Liguria", addizionale: 1.23 },
    marche: { name: "Marche", addizionale: 1.73 },
    abruzzo: { name: "Abruzzo", addizionale: 1.73 },
    friuli: { name: "Friuli-Venezia Giulia", addizionale: 0.7 },
    umbria: { name: "Umbria", addizionale: 1.23 },
    sardegna: { name: "Sardegna", addizionale: 1.23 },
    trentino: { name: "Trentino-Alto Adige", addizionale: 1.23 },
    basilicata: { name: "Basilicata", addizionale: 1.23 },
    molise: { name: "Molise", addizionale: 2.03 },
    valdaosta: { name: "Valle d'Aosta", addizionale: 1.23 },
  };

  const PENSION_DEDUCTION_CAP = 5164.57;
  const REGIONAL_AVG = 1.73;
  const MUNICIPAL_AVG = 0.8;
  const MONTH_NAMES = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
  const API_BASE = process.env.REACT_APP_API_BASE || "/api";
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  const AUTH_TOKEN_KEY = "finanziariamente_jwt";

  const charts = {};
  const pendingCharts = {};
  const state = {
    fmType: "house",
    latest: {
      loan: null,
      pension: null,
      salary: null,
      financeManager: null,
      wealth: null,
    },
    auth: {
      token: localStorage.getItem(AUTH_TOKEN_KEY),
      user: null,
      saveTimer: null,
      googleInitialized: false,
    },
    isHydratingCloudState: false,
  };

  const euroFormatter = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 });

  const q = (id) => document.getElementById(id);
  const num = (id, fallback = 0) => {
    const value = parseFloat(q(id).value);
    return Number.isFinite(value) ? value : fallback;
  };
  const str = (id) => q(id).value;
  const checked = (id) => q(id).checked;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const euro = (value) => `€${euroFormatter.format(Math.round(value || 0))}`;
  const percent = (value, digits = 1) => `${Number(value || 0).toFixed(digits)}%`;
  const timeLabel = () =>
    new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

  function setAuthMessage(message, type = "") {
    const el = q("auth-message");
    if (!el) {
      return;
    }
    el.textContent = message || "";
    el.className = `auth-message ${type}`.trim();
  }

  function setAuthStatus(message, mode = "muted") {
    const el = q("auth-sync-status");
    if (!el) {
      return;
    }
    el.textContent = message;
    el.className = `auth-status ${mode}`;
  }

  function updateAuthUi() {
    const openBtn = q("auth-open-btn");
    const logoutBtn = q("auth-logout-btn");

    if (!openBtn || !logoutBtn) {
      return;
    }

    if (state.auth.user) {
      openBtn.textContent = state.auth.user.name || state.auth.user.email;
      logoutBtn.classList.remove("hidden");
      setAuthStatus(`Cloud attivo • ${state.auth.user.email}`, "ok");
    } else {
      openBtn.textContent = "Accedi";
      logoutBtn.classList.add("hidden");
      setAuthStatus("Sessione offline", "muted");
    }
  }

  async function apiRequest(path, { method = "GET", body, auth = true } = {}) {
    const headers = { "Content-Type": "application/json" };
    if (auth && state.auth.token) {
      headers.Authorization = `Bearer ${state.auth.token}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      credentials: "omit",
      body: body ? JSON.stringify(body) : undefined,
    });

    let payload = {};
    try {
      payload = await response.json();
    } catch (_err) {
      payload = {};
    }

    if (!response.ok) {
      const message =
        response.status === 431
          ? "Richiesta troppo grande (431). Svuota i cookie di localhost oppure usa 127.0.0.1:3000."
          : payload.error || `Errore richiesta (${response.status})`;
      throw new Error(message);
    }

    return payload;
  }

  function openAuthModal() {
    q("auth-modal").classList.remove("hidden");
  }

  function closeAuthModal() {
    q("auth-modal").classList.add("hidden");
  }

  function setLoggedUser(authPayload) {
    state.auth.token = authPayload.token;
    state.auth.user = authPayload.user;
    localStorage.setItem(AUTH_TOKEN_KEY, authPayload.token);
    updateAuthUi();
  }

  function clearLoggedUser() {
    state.auth.token = null;
    state.auth.user = null;
    localStorage.removeItem(AUTH_TOKEN_KEY);
    updateAuthUi();
  }

  function collectAppState() {
    const fields = {};
    document.querySelectorAll("main input[id], main select[id]").forEach((el) => {
      if (el.id.startsWith("auth-")) {
        return;
      }
      if (el.type === "checkbox") {
        fields[el.id] = el.checked;
      } else {
        fields[el.id] = el.value;
      }
    });

    const mode = document.querySelector("input[name='loan-mode']:checked");
    if (mode) {
      fields.loanMode = mode.value;
    }

    return {
      version: 1,
      fields,
      ui: {
        page: q("manager-section").classList.contains("active") ? "manager" : "home",
        homePanel: document.querySelector("#home-section .tab-panel.active")?.id || "panel-loans",
        managerPanel: state.fmType,
      },
      savedAt: new Date().toISOString(),
    };
  }

  function applyAppState(appState) {
    if (!appState || typeof appState !== "object") {
      return;
    }

    state.isHydratingCloudState = true;
    const fields = appState.fields || {};

    Object.entries(fields).forEach(([id, value]) => {
      if (id === "loanMode") {
        return;
      }
      const el = q(id);
      if (!el) {
        return;
      }
      if (el.type === "checkbox") {
        el.checked = Boolean(value);
      } else {
        el.value = String(value);
      }
    });

    if (fields.loanMode) {
      const selectedMode = document.querySelector(`input[name='loan-mode'][value='${fields.loanMode}']`);
      if (selectedMode) {
        selectedMode.checked = true;
      }
    }

    if (appState.ui?.page === "manager") {
      q("show-manager").click();
    } else {
      q("show-home").click();
    }

    if (appState.ui?.homePanel) {
      const tabButton = document.querySelector(`#home-section .tab[data-target='${appState.ui.homePanel}']`);
      if (tabButton) {
        tabButton.click();
      }
    }

    if (appState.ui?.managerPanel === "car") {
      q("fm-tab-car").click();
    } else if (appState.ui?.managerPanel === "house") {
      q("fm-tab-house").click();
    }

    state.isHydratingCloudState = false;
    refreshAllCalculators();
  }

  function scheduleCloudSync() {
    if (!state.auth.user || state.isHydratingCloudState) {
      return;
    }

    if (state.auth.saveTimer) {
      clearTimeout(state.auth.saveTimer);
    }

    setAuthStatus("Sincronizzazione...", "pending");
    state.auth.saveTimer = setTimeout(async () => {
      try {
        await apiRequest("/state", {
          method: "PUT",
          body: { appState: collectAppState() },
        });
        setAuthStatus(`Salvato ${timeLabel()}`, "ok");
      } catch (err) {
        setAuthStatus(`Errore sync: ${err.message}`, "pending");
      }
    }, 700);
  }

  async function loadCloudState() {
    if (!state.auth.user) {
      return;
    }
    try {
      const data = await apiRequest("/state");
      if (data.state?.appState) {
        applyAppState(data.state.appState);
      } else {
        setAuthStatus(`Cloud attivo • nessun salvataggio`, "ok");
      }
    } catch (err) {
      setAuthStatus(`Errore caricamento: ${err.message}`, "pending");
    }
  }

  function refreshAllCalculators() {
    updateLoan();
    updatePension();
    updateSalary();
    updateFinanceManager();
    updateWealthManager();
    refreshCharts();
  }

  function renderGoogleLoginButton() {
    if (state.auth.googleInitialized) {
      return;
    }

    if (!GOOGLE_CLIENT_ID) {
      setAuthMessage("Google login non configurato: imposta REACT_APP_GOOGLE_CLIENT_ID.", "error");
      return;
    }

    let attempts = 0;
    const maxAttempts = 20;

    const tryInit = () => {
      if (!window.google?.accounts?.id) {
        attempts += 1;
        if (attempts <= maxAttempts) {
          setTimeout(tryInit, 250);
        } else {
          setAuthMessage("Impossibile inizializzare Google login.", "error");
        }
        return;
      }

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          try {
            const payload = await apiRequest("/auth/google", {
              method: "POST",
              auth: false,
              body: { credential: response.credential },
            });
            setLoggedUser(payload);
            closeAuthModal();
            setAuthMessage("Accesso Google completato.", "success");
            await loadCloudState();
          } catch (err) {
            setAuthMessage(err.message, "error");
          }
        },
      });

      const slot = q("google-login-slot");
      if (slot) {
        slot.innerHTML = "";
        window.google.accounts.id.renderButton(slot, {
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "pill",
          width: 240,
        });
      }

      state.auth.googleInitialized = true;
    };

    tryInit();
  }

  async function hydrateSessionFromToken() {
    if (!state.auth.token) {
      updateAuthUi();
      return;
    }

    try {
      const data = await apiRequest("/auth/me");
      state.auth.user = data.user;
      updateAuthUi();
      await loadCloudState();
    } catch (_err) {
      clearLoggedUser();
    }
  }

  function progressiveTax(income) {
    let tax = 0;
    let remaining = Math.max(0, income);
    let previousLimit = 0;

    for (const bracket of TAX_BRACKETS) {
      const taxableSlice = Math.min(remaining, bracket.limit - previousLimit);
      if (taxableSlice <= 0) {
        break;
      }
      tax += taxableSlice * bracket.rate;
      remaining -= taxableSlice;
      previousLimit = bracket.limit;
    }

    return tax;
  }

  function progressiveTaxWithBreakdown(income) {
    let tax = 0;
    let remaining = Math.max(0, income);
    let previousLimit = 0;
    const breakdown = [];

    for (const bracket of TAX_BRACKETS) {
      const taxableSlice = Math.min(remaining, bracket.limit - previousLimit);
      if (taxableSlice <= 0) {
        break;
      }

      const bracketTax = taxableSlice * bracket.rate;
      tax += bracketTax;

      breakdown.push({
        range:
          previousLimit === 0
            ? `0 - €${euroFormatter.format(bracket.limit)}`
            : `€${euroFormatter.format(previousLimit)} - €${
                bracket.limit === Infinity ? "∞" : euroFormatter.format(bracket.limit)
              }`,
        rate: `${(bracket.rate * 100).toFixed(0)}%`,
        taxable: Math.round(taxableSlice),
        tax: Math.round(bracketTax),
      });

      remaining -= taxableSlice;
      previousLimit = bracket.limit;
    }

    return { tax, bracketBreakdown: breakdown };
  }

  function detrazioniLavoro(gross, dependents = 0) {
    let detrazioni = 0;

    if (gross <= 15000) {
      detrazioni = 1955;
    } else if (gross <= 28000) {
      detrazioni = 1910 + 1190 * ((28000 - gross) / 13000);
    } else if (gross <= 50000) {
      detrazioni = 1910 * ((50000 - gross) / 22000);
    }

    if (gross <= 28000) {
      detrazioni += 1200;
    }

    if (dependents > 0) {
      detrazioni += dependents * 750;
    }

    return Math.round(detrazioni);
  }

  function marginalRate(taxableIncome) {
    if (taxableIncome <= 28000) {
      return 0.23;
    }
    if (taxableIncome <= 50000) {
      return 0.35;
    }
    return 0.43;
  }

  function monthlyPayment(principal, annualRatePct, years) {
    const p = Math.max(0, principal);
    const n = Math.max(1, years * 12);
    const monthlyRate = Math.max(0, annualRatePct) / 100 / 12;

    if (p <= 0) {
      return 0;
    }

    if (monthlyRate === 0) {
      return p / n;
    }

    return (p * (monthlyRate * Math.pow(1 + monthlyRate, n))) / (Math.pow(1 + monthlyRate, n) - 1);
  }

  function annualNetForLoanAndMax(gross) {
    const inps = gross * 0.0919;
    const taxable = gross - inps;
    const irpef = progressiveTax(taxable);
    return gross - irpef - inps;
  }

  function annualNetForFinanceManager(gross) {
    const inps = gross * 0.0919;
    const taxable = gross - inps;
    const irpef = progressiveTax(taxable);
    const detrazioni = detrazioniLavoro(gross, 0);

    const irpefNet = Math.max(0, irpef - detrazioni);
    const regional = taxable * 0.0173;
    const municipal = taxable * 0.008;
    return gross - inps - irpefNet - regional - municipal;
  }

  function monthlyNetWithMensilita(annualNet, mensilita, monthIndex) {
    const installments = clamp(Math.round(mensilita || 13), 12, 14);
    const baseInstallment = annualNet / installments;
    const has13th = installments >= 13;
    const has14th = installments === 14;
    let installmentCount = 1;

    if (has13th && monthIndex === 11) {
      installmentCount += 1;
    }
    if (has14th && monthIndex === 5) {
      installmentCount += 1;
    }

    return baseInstallment * installmentCount;
  }

  function setChart(key, canvasId, config) {
    const canvas = q(canvasId);
    if (!canvas || typeof Chart === "undefined") {
      return;
    }

    const isVisible = canvas.offsetParent !== null && canvas.getClientRects().length > 0 && canvas.clientWidth > 0;
    if (!isVisible) {
      pendingCharts[key] = { canvasId, config };
      return;
    }

    if (pendingCharts[key]) {
      delete pendingCharts[key];
    }

    if (charts[key]) {
      charts[key].destroy();
    }

    charts[key] = new Chart(canvas, config);
  }

  function refreshCharts() {
    requestAnimationFrame(() => {
      Object.entries(pendingCharts).forEach(([key, payload]) => {
        const canvas = q(payload.canvasId);
        const visible = canvas && canvas.offsetParent !== null && canvas.getClientRects().length > 0 && canvas.clientWidth > 0;
        if (visible) {
          setChart(key, payload.canvasId, payload.config);
        }
      });
    });
  }

  function refreshActiveHomePanel() {
    const activePanel = document.querySelector("#home-section .tab-panel.active");
    if (!activePanel) {
      return;
    }

    if (activePanel.id === "panel-loans") {
      updateLoan();
    } else if (activePanel.id === "panel-pension") {
      updatePension();
    } else if (activePanel.id === "panel-salary") {
      updateSalary();
    } else if (activePanel.id === "panel-wealth") {
      updateWealthManager();
    }

    refreshCharts();
  }

  function wirePageNavigation() {
    q("show-home").addEventListener("click", () => setPage("home"));
    q("show-manager").addEventListener("click", () => setPage("manager"));

    function setPage(page) {
      const isHome = page === "home";
      q("home-section").classList.toggle("active", isHome);
      q("manager-section").classList.toggle("active", !isHome);
      q("show-home").classList.toggle("active", isHome);
      q("show-manager").classList.toggle("active", !isHome);

      if (isHome) {
        refreshActiveHomePanel();
      } else {
        updateFinanceManager();
        refreshCharts();
      }
      scheduleCloudSync();
    }
  }

  function wireCalculatorTabs() {
    const buttons = document.querySelectorAll("#home-section .tabs .tab");
    buttons.forEach((btn) => {
      btn.addEventListener("click", () => {
        buttons.forEach((b) => b.classList.remove("active"));
        btn.classList.add("active");

        document.querySelectorAll("#home-section .tab-panel").forEach((panel) => {
          panel.classList.remove("active");
        });
        const targetPanelId = btn.dataset.target;
        q(targetPanelId).classList.add("active");

        if (targetPanelId === "panel-loans") {
          updateLoan();
        } else if (targetPanelId === "panel-pension") {
          updatePension();
        } else if (targetPanelId === "panel-salary") {
          updateSalary();
        } else if (targetPanelId === "panel-wealth") {
          updateWealthManager();
        }

        refreshCharts();
        scheduleCloudSync();
      });
    });
  }

  function wireManagerTabs() {
    const houseBtn = q("fm-tab-house");
    const carBtn = q("fm-tab-car");

    houseBtn.addEventListener("click", () => setManagerType("house"));
    carBtn.addEventListener("click", () => setManagerType("car"));

    function setManagerType(type) {
      state.fmType = type;
      houseBtn.classList.toggle("active", type === "house");
      carBtn.classList.toggle("active", type === "car");
      q("fm-house").classList.toggle("active", type === "house");
      q("fm-car").classList.toggle("active", type === "car");
      updateFinanceManager();
      refreshCharts();
      scheduleCloudSync();
    }
  }

  function populateSalaryRegions() {
    const regionSelect = q("salary-region");
    const regionEntries = Object.entries(SALARY_REGIONS).sort((a, b) => a[1].name.localeCompare(b[1].name));

    regionSelect.innerHTML = regionEntries
      .map(([id, region]) => `<option value="${id}">${region.name} (${region.addizionale}%)</option>`)
      .join("");

    regionSelect.value = "lombardia";
  }

  function setupListeners() {
    const loanIds = ["loan-ral", "loan-country", "loan-years", "loan-rate", "loan-requested"];
    loanIds.forEach((id) => q(id).addEventListener("input", updateLoan));
    document.querySelectorAll("input[name='loan-mode']").forEach((radio) =>
      radio.addEventListener("change", () => {
        updateLoan();
        scheduleCloudSync();
      })
    );

    const pensionIds = ["pension-ral", "pension-sector", "pension-contrib", "pension-age", "pension-retirement", "pension-months"];
    pensionIds.forEach((id) => q(id).addEventListener("input", updatePension));

    const salaryIds = [
      "salary-ral",
      "salary-months",
      "salary-region",
      "salary-contract",
      "salary-dependents",
      "salary-bonus",
      "salary-bonus-enabled",
    ];
    salaryIds.forEach((id) => q(id).addEventListener("input", updateSalary));

    const fmIds = [
      "fm-liquidity",
      "fm-ral",
      "fm-months",
      "fm-fixed-expenses",
      "fm-house-first-home",
      "fm-house-value",
      "fm-house-mortgage",
      "fm-house-years",
      "fm-house-rate",
      "fm-house-agency",
      "fm-house-notary",
      "fm-house-condo-on",
      "fm-house-condo",
      "fm-house-heating-on",
      "fm-house-heating",
      "fm-purchase-month",
      "fm-car-value",
      "fm-car-down-payment",
      "fm-car-years",
      "fm-car-rate",
      "fm-car-insurance",
      "fm-car-maintenance",
    ];

    fmIds.forEach((id) => q(id).addEventListener("input", updateFinanceManager));

    const wealthIds = [
      "wealth-current-assets",
      "wealth-monthly-savings",
      "wealth-horizon-years",
      "wealth-risk-profile",
      "wealth-emergency-target",
      "wealth-extra-investment",
    ];
    wealthIds.forEach((id) => q(id).addEventListener("input", updateWealthManager));

    document.querySelectorAll("main input[id], main select[id]").forEach((el) => {
      const eventName = el.tagName === "SELECT" || el.type === "checkbox" ? "change" : "input";
      el.addEventListener(eventName, scheduleCloudSync);
    });
  }

  function wireAuthListeners() {
    q("auth-open-btn").addEventListener("click", () => {
      setAuthMessage("");
      openAuthModal();
      renderGoogleLoginButton();
    });
    q("auth-close-btn").addEventListener("click", closeAuthModal);
    q("auth-modal").addEventListener("click", (event) => {
      if (event.target.id === "auth-modal") {
        closeAuthModal();
      }
    });

    q("auth-logout-btn").addEventListener("click", () => {
      clearLoggedUser();
      setAuthMessage("Logout completato.", "success");
      scheduleCloudSync();
    });

    q("auth-login-submit").addEventListener("click", async () => {
      const email = q("auth-email").value.trim();
      const password = q("auth-password").value;

      try {
        const payload = await apiRequest("/auth/login", {
          method: "POST",
          auth: false,
          body: { email, password },
        });
        setLoggedUser(payload);
        setAuthMessage("Login effettuato con successo.", "success");
        closeAuthModal();
        await loadCloudState();
      } catch (err) {
        setAuthMessage(err.message, "error");
      }
    });

    q("auth-register-submit").addEventListener("click", async () => {
      const name = q("auth-name").value.trim();
      const email = q("auth-email").value.trim();
      const password = q("auth-password").value;

      try {
        const payload = await apiRequest("/auth/register", {
          method: "POST",
          auth: false,
          body: { name, email, password },
        });
        setLoggedUser(payload);
        setAuthMessage("Registrazione completata.", "success");
        closeAuthModal();
        await loadCloudState();
      } catch (err) {
        setAuthMessage(err.message, "error");
      }
    });
  }

  function loadPensionSettings() {
    const defaults = {
      pension_ral: 35000,
      pension_sector: "commercio",
      pension_contrib: 2,
      pension_age: 30,
      pension_retirement: 67,
      pension_months: 13,
    };

    for (const [key, fallback] of Object.entries(defaults)) {
      const raw = localStorage.getItem(key);
      if (raw === null) {
        continue;
      }

      if (typeof fallback === "number") {
        q(key.replace("pension_", "pension-")).value = Number(raw);
      } else {
        q(key.replace("pension_", "pension-")).value = raw;
      }
    }
  }

  function savePensionSettings(values) {
    localStorage.setItem("pension_ral", String(values.ral));
    localStorage.setItem("pension_sector", values.sectorId);
    localStorage.setItem("pension_contrib", String(values.contribPercent));
    localStorage.setItem("pension_age", String(values.age));
    localStorage.setItem("pension_retirement", String(values.retirementAge));
    localStorage.setItem("pension_months", String(values.months));
  }

  function updateLoan() {
    const mode = document.querySelector("input[name='loan-mode']:checked").value;
    const ral = Math.max(0, num("loan-ral", 35000));
    const countryId = str("loan-country");
    const country = LOAN_COUNTRIES[countryId] ?? LOAN_COUNTRIES.italy;
    const requestedLoan = Math.max(0, num("loan-requested", 150000));
    const rate = Math.max(0, num("loan-rate", country.avgRate));

    let years = Math.round(num("loan-years", 20));
    years = clamp(years, 5, country.maxYears);
    q("loan-years").value = String(years);

    q("loan-country-wrap").classList.toggle("hidden", mode !== "max");
    q("loan-specific-wrap").classList.toggle("hidden", mode !== "specific");

    const netMonthly = annualNetForLoanAndMax(ral) / 12;
    const maxMonthlyPayment = netMonthly * country.maxDebtRatio;

    const monthlyRate = rate / 100 / 12;
    const nMonths = years * 12;

    const maxLoan =
      monthlyRate === 0
        ? maxMonthlyPayment * nMonths
        : maxMonthlyPayment * ((1 - Math.pow(1 + monthlyRate, -nMonths)) / monthlyRate);

    const specificMonthlyPayment = monthlyPayment(requestedLoan, rate, years);
    const specificTotalInterest = specificMonthlyPayment * nMonths - requestedLoan;
    const specificTotalPayment = specificMonthlyPayment * nMonths;
    const debtToIncomeRatio = netMonthly > 0 ? (specificMonthlyPayment / netMonthly) * 100 : 0;
    const isAffordable = specificMonthlyPayment <= maxMonthlyPayment;

    const principal = mode === "max" ? maxLoan : requestedLoan;
    const annualPayment = mode === "max" ? maxMonthlyPayment : specificMonthlyPayment;
    const amortizationData = [];

    let remaining = principal;
    for (let year = 0; year <= years; year += 1) {
      const yearInterest = year === 0 ? 0 : remaining * (rate / 100);
      const annualPrincipal = year === 0 ? 0 : Math.min(annualPayment * 12 - yearInterest, remaining);
      remaining = Math.max(0, remaining - annualPrincipal);

      amortizationData.push({
        year: `Anno ${year}`,
        capitale: Math.round(principal - remaining),
        interessi: Math.round(year * yearInterest),
        debito: Math.round(remaining),
      });
    }

    const totalInterest = annualPayment * nMonths - principal;
    const totalPayment = annualPayment * nMonths;

    const countryComparison = Object.entries(LOAN_COUNTRIES).map(([id, data]) => {
      const monthlyCapacity = annualNetForLoanAndMax(ral) / 12 * data.maxDebtRatio;
      const mRate = data.avgRate / 100 / 12;
      const n = 20 * 12;
      const max = mRate === 0 ? monthlyCapacity * n : monthlyCapacity * ((1 - Math.pow(1 + mRate, -n)) / mRate);

      return {
        country: data.name,
        maxLoan: Math.round(max),
        color: id === countryId ? "#0f766e" : "#94a3b8",
      };
    });

    const stat4 = q("loan-stat-4");

    if (mode === "max") {
      stat4.classList.add("hidden");

      q("loan-stat-1-label").textContent = "Prestito Massimo";
      q("loan-stat-1-value").textContent = euro(maxLoan);
      q("loan-stat-1-sub").textContent = `Basato sul ${(country.maxDebtRatio * 100).toFixed(0)}% del netto`;

      q("loan-stat-2-label").textContent = "Rata Mensile Max";
      q("loan-stat-2-value").textContent = euro(maxMonthlyPayment);
      q("loan-stat-2-sub").textContent = `Su netto di ${euro(netMonthly)}/mese`;

      q("loan-stat-3-label").textContent = "Totale Interessi";
      q("loan-stat-3-value").textContent = euro(totalInterest);
      q("loan-stat-3-sub").textContent = `Totale pagato: ${euro(totalPayment)}`;
    } else {
      stat4.classList.remove("hidden");

      q("loan-stat-1-label").textContent = "Rata Mensile";
      q("loan-stat-1-value").textContent = euro(specificMonthlyPayment);
      q("loan-stat-1-sub").textContent = isAffordable ? "Sostenibile" : "Oltre il limite";

      q("loan-stat-2-label").textContent = "Rapporto Rata/Netto";
      q("loan-stat-2-value").textContent = percent(debtToIncomeRatio, 1);
      q("loan-stat-2-sub").textContent = "Max consigliato: 33%";

      q("loan-stat-3-label").textContent = "Totale Interessi";
      q("loan-stat-3-value").textContent = euro(specificTotalInterest);
      q("loan-stat-3-sub").textContent = `${requestedLoan > 0 ? ((specificTotalInterest / requestedLoan) * 100).toFixed(0) : 0}% del capitale`;

      q("loan-stat-4-label").textContent = "Totale da Rimborsare";
      q("loan-stat-4-value").textContent = euro(specificTotalPayment);
      q("loan-stat-4-sub").textContent = `Capitale: ${euro(requestedLoan)}`;
    }

    setChart("loanAmortization", "loan-amortization-chart", {
      type: "line",
      data: {
        labels: amortizationData.map((d) => d.year),
        datasets: [
          {
            label: "Capitale rimborsato",
            data: amortizationData.map((d) => d.capitale),
            borderColor: "#0f766e",
            backgroundColor: "rgba(15, 118, 110, 0.15)",
            fill: true,
            tension: 0.22,
            pointRadius: 1.8,
          },
          {
            label: "Debito residuo",
            data: amortizationData.map((d) => d.debito),
            borderColor: "#f97316",
            backgroundColor: "transparent",
            tension: 0.22,
            pointRadius: 1.8,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2.6,
        plugins: {
          legend: { position: "bottom" },
        },
        scales: {
          y: {
            ticks: { callback: (v) => `€${Math.round(v / 1000)}k` },
          },
        },
      },
    });

    setChart("loanCountry", "loan-country-chart", {
      type: "bar",
      data: {
        labels: countryComparison.map((c) => c.country),
        datasets: [
          {
            label: "Prestito massimo",
            data: countryComparison.map((c) => c.maxLoan),
            backgroundColor: countryComparison.map((c) => c.color),
            borderRadius: 8,
          },
        ],
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2.5,
        plugins: {
          legend: { display: false },
        },
        scales: {
          x: {
            ticks: { callback: (v) => `€${Math.round(v / 1000)}k` },
          },
        },
      },
    });

    state.latest.loan = {
      mode,
      netMonthly,
      maxMonthlyPayment,
      maxLoan,
      specificMonthlyPayment,
      debtToIncomeRatio: Number(debtToIncomeRatio.toFixed(1)),
      isAffordable,
    };

    updateWealthManager();
  }

  function updatePension() {
    const ral = Math.max(0, num("pension-ral", 35000));
    const sectorId = str("pension-sector");
    const sector = PENSION_SECTORS[sectorId] ?? PENSION_SECTORS.commercio;

    let contribPercent = num("pension-contrib", 2);
    contribPercent = clamp(contribPercent, sector.minEmployee, 10);
    q("pension-contrib").value = String(contribPercent);

    let age = Math.round(num("pension-age", 30));
    age = clamp(age, 18, 65);
    q("pension-age").value = String(age);

    let retirementAge = Math.round(num("pension-retirement", 67));
    retirementAge = clamp(retirementAge, 60, 70);
    if (retirementAge < age) {
      retirementAge = age;
    }
    q("pension-retirement").value = String(retirementAge);

    const months = parseInt(str("pension-months"), 10) || 13;

    savePensionSettings({ ral, sectorId, contribPercent, age, retirementAge, months });

    const grossMonthly = ral / 12;
    const inps = ral * 0.0919;
    const employeeContrib = ral * (contribPercent / 100);
    const employerContrib = ral * (sector.employerContrib / 100);
    const totalPensionContrib = employeeContrib + employerContrib;

    const deductibleAmount = Math.min(employeeContrib, PENSION_DEDUCTION_CAP);
    const taxableWithoutPension = ral - inps;
    const taxesWithoutPension =
      progressiveTax(taxableWithoutPension) + taxableWithoutPension * (REGIONAL_AVG / 100) + taxableWithoutPension * (MUNICIPAL_AVG / 100);

    const taxableWithPension = ral - inps - deductibleAmount;
    const taxesWithPension =
      progressiveTax(taxableWithPension) + taxableWithPension * (REGIONAL_AVG / 100) + taxableWithPension * (MUNICIPAL_AVG / 100);

    const taxSavings = taxesWithoutPension - taxesWithPension;
    const netWithout = (ral - inps - taxesWithoutPension) / months;
    const netWith = (ral - inps - employeeContrib - taxesWithPension) / months;

    const actualCost = netWithout - netWith;
    const effectiveCost = employeeContrib / months - taxSavings / months;
    const yearsToRetirement = Math.max(0, retirementAge - age);
    const yearlyReturn = 0.04;

    const availableForOptimization = Math.max(0, PENSION_DEDUCTION_CAP - employerContrib);
    const optimalPercent = Math.min((availableForOptimization / Math.max(1, ral)) * 100, 10);
    const optimalContrib = Math.min((ral * optimalPercent) / 100, availableForOptimization);

    let futureValue = 0;
    const projection = [];
    for (let year = 0; year <= yearsToRetirement; year += 1) {
      futureValue = futureValue * (1 + yearlyReturn) + totalPensionContrib;
      if (year % 5 === 0 || year === yearsToRetirement) {
        projection.push({
          year: age + year,
          valore: Math.round(futureValue),
          contributi: Math.round(totalPensionContrib * year),
        });
      }
    }

    const totalContrib = employeeContrib + employerContrib;
    const nearCap = Math.abs(totalContrib - PENSION_DEDUCTION_CAP) < 100;
    const overCap = totalContrib > PENSION_DEDUCTION_CAP;

    const alert = q("pension-alert");
    alert.className = "callout";

    if (nearCap) {
      alert.classList.add("success");
      alert.innerHTML =
        "<strong>Perfetto:</strong> stai massimizzando il beneficio fiscale con il limite di deducibilità di €5.164,57 (tuo + datore).";
    } else if (overCap) {
      alert.classList.add("warning");
      alert.innerHTML = `<strong>Attenzione:</strong> il totale contributi (${euro(employeeContrib)} + ${euro(
        employerContrib
      )} = ${euro(totalContrib)}) supera il limite di €5.164,57. Contributo ottimale: <strong>${percent(
        optimalPercent,
        1
      )}</strong>.`;
    } else {
      alert.classList.add("info");
      alert.innerHTML = `<strong>Suggerimento:</strong> potresti aumentare al <strong>${percent(
        optimalPercent,
        1
      )}</strong> per raggiungere il limite di deducibilità, considerando il contributo datore di ${euro(employerContrib)}.`;
    }

    q("pension-tax-savings").textContent = euro(taxSavings);
    q("pension-tax-rate").textContent = `Aliquota marginale: ${percent(marginalRate(taxableWithoutPension) * 100, 0)}`;
    q("pension-net-without").textContent = euro(netWithout);
    q("pension-net-with").textContent = euro(netWith);
    q("pension-actual-cost").textContent = `-${euro(actualCost)}/mese`;
    q("pension-future-label").textContent = `Montante a ${retirementAge} anni`;
    q("pension-future-value").textContent = euro(futureValue);
    q("pension-years-left").textContent = `In ${yearsToRetirement} anni`;
    q("pension-total-contrib").textContent = euro(totalPensionContrib);
    q("pension-effective-cost-note").innerHTML = `Versando <strong>${euro(
      employeeContrib / months
    )}/mese</strong> nel fondo pensione, il costo effettivo è solo <strong>${euro(effectiveCost)}/mese</strong> grazie al risparmio fiscale.`;

    setChart("pensionBreakdown", "pension-breakdown-chart", {
      type: "doughnut",
      data: {
        labels: ["Tuo contributo", "Contributo datore"],
        datasets: [
          {
            data: [Math.round(employeeContrib), Math.round(employerContrib)],
            backgroundColor: ["#0f766e", "#2563eb"],
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1.75,
        plugins: {
          legend: { position: "bottom" },
        },
      },
    });

    setChart("pensionProjection", "pension-projection-chart", {
      type: "line",
      data: {
        labels: projection.map((d) => String(d.year)),
        datasets: [
          {
            label: "Valore fondo",
            data: projection.map((d) => d.valore),
            borderColor: "#8b5cf6",
            backgroundColor: "rgba(139, 92, 246, 0.2)",
            tension: 0.25,
            fill: true,
          },
          {
            label: "Solo contributi",
            data: projection.map((d) => d.contributi),
            borderColor: "#94a3b8",
            borderDash: [6, 4],
            tension: 0.2,
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2.5,
        plugins: {
          legend: { position: "bottom" },
        },
        scales: {
          y: {
            ticks: { callback: (v) => `€${Math.round(v / 1000)}k` },
          },
        },
      },
    });

    setChart("pensionImpact", "pension-impact-chart", {
      type: "bar",
      data: {
        labels: ["Senza FP", "Con FP"],
        datasets: [
          {
            label: "Netto in busta",
            data: [Math.round(netWithout), Math.round(netWith)],
            backgroundColor: ["#64748b", "#0f766e"],
            borderRadius: 8,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2.9,
        plugins: {
          legend: { display: false },
        },
      },
    });

    state.latest.pension = {
      taxSavings: Math.round(taxSavings),
      netWithout: Math.round(netWithout),
      netWith: Math.round(netWith),
      effectiveCost: Math.round(effectiveCost),
      futureValue: Math.round(futureValue),
      yearsToRetirement,
      optimalContrib: Math.round(optimalContrib),
    };

    // Keep unused values around to match original feature set.
    void grossMonthly;
    updateWealthManager();
  }

  function updateSalary() {
    const ral = Math.max(0, num("salary-ral", 35000));
    const months = parseInt(str("salary-months"), 10) || 13;
    const regionId = str("salary-region");
    const region = SALARY_REGIONS[regionId] ?? SALARY_REGIONS.lombardia;
    const dependents = parseInt(str("salary-dependents"), 10) || 0;
    const bonusEnabled = checked("salary-bonus-enabled");
    const bonus = Math.max(0, num("salary-bonus", 3000));
    const contract = str("salary-contract");

    q("salary-bonus").classList.toggle("hidden", !bonusEnabled);

    const totalGross = ral + (bonusEnabled ? bonus : 0);
    const inpsRate = contract === "dipendente" ? 0.0919 : 0.2572;
    const inps = totalGross * inpsRate;
    const taxableIncome = totalGross - inps;

    const { tax: irpef, bracketBreakdown } = progressiveTaxWithBreakdown(taxableIncome);
    const deductions = detrazioniLavoro(ral, dependents);
    const irpefNet = Math.max(0, irpef - deductions);

    const regionalTax = taxableIncome * (region.addizionale / 100);
    const municipalTax = taxableIncome * (MUNICIPAL_AVG / 100);

    const totalTaxes = irpefNet + regionalTax + municipalTax;
    const netYearly = totalGross - inps - totalTaxes;
    const netMonthly = netYearly / months;
    const grossMonthly = totalGross / months;

    const effectiveTaxRate = totalGross > 0 ? (totalTaxes / totalGross) * 100 : 0;
    const totalDeductionRate = totalGross > 0 ? ((inps + totalTaxes) / totalGross) * 100 : 0;

    const monthlyBase = netYearly / 12;
    const monthlyBreakdown = MONTH_NAMES.map((name, index) => {
      let value = monthlyBase;
      let label = name;

      if (index === 5 && months === 14) {
        value = monthlyBase * 2;
        label = "Giu + 14ª";
      }

      if (index === 11 && months >= 13) {
        value = monthlyBase * 2;
        label = "Dic + 13ª";
      }

      return { month: label, netto: Math.round(value) };
    });

    q("salary-net-monthly").textContent = euro(netMonthly);
    q("salary-net-monthly-sub").textContent = `${months} mensilità • Lordo: ${euro(grossMonthly)}/mese`;
    q("salary-net-yearly").textContent = euro(netYearly);
    q("salary-net-yearly-sub").textContent = `Su ${euro(totalGross)} lordi`;
    q("salary-effective-tax").textContent = percent(effectiveTaxRate, 1);
    q("salary-total-deduction").textContent = `Tot. trattenute: ${percent(totalDeductionRate, 1)}`;

    q("salary-deductions").textContent = `-${euro(deductions)}`;

    const bracketContainer = q("salary-brackets");
    bracketContainer.innerHTML = bracketBreakdown
      .map(
        (row) =>
          `<div class="list-item"><div><strong>${row.rate}</strong><br><small>${row.range} • Imponibile: ${euro(
            row.taxable
          )}</small></div><strong>${euro(row.tax)}</strong></div>`
      )
      .join("");

    q("salary-summary-inps").textContent = euro(inps);
    q("salary-summary-inps-rate").textContent = `${(inpsRate * 100).toFixed(2)}% del lordo`;
    q("salary-summary-irpef").textContent = euro(irpefNet);
    q("salary-summary-detr").textContent = `Dopo detrazioni di ${euro(deductions)}`;
    q("salary-summary-regional").textContent = euro(regionalTax);
    q("salary-summary-region-name").textContent = `${region.name}: ${region.addizionale}%`;
    q("salary-summary-municipal").textContent = euro(municipalTax);
    q("salary-summary-municipal-rate").textContent = `Media: ${MUNICIPAL_AVG}%`;

    setChart("salaryBreakdown", "salary-breakdown-chart", {
      type: "doughnut",
      data: {
        labels: ["Netto", "IRPEF", "INPS", "Add. Regionale", "Add. Comunale"],
        datasets: [
          {
            data: [
              Math.round(netYearly),
              Math.round(irpefNet),
              Math.round(inps),
              Math.round(regionalTax),
              Math.round(municipalTax),
            ],
            backgroundColor: ["#0f766e", "#ef4444", "#f97316", "#8b5cf6", "#ec4899"],
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1.75,
        plugins: {
          legend: { position: "bottom" },
        },
      },
    });

    setChart("salaryMonthly", "salary-monthly-chart", {
      type: "bar",
      data: {
        labels: monthlyBreakdown.map((m) => m.month),
        datasets: [
          {
            label: "Netto",
            data: monthlyBreakdown.map((m) => m.netto),
            backgroundColor: "#0f766e",
            borderRadius: 5,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2.6,
        plugins: {
          legend: { display: false },
        },
      },
    });

    state.latest.salary = {
      netYearly: Math.round(netYearly),
      netMonthly: Math.round(netMonthly),
      totalGross: Math.round(totalGross),
      totalTaxes: Math.round(totalTaxes),
      inps: Math.round(inps),
      effectiveTaxRate: Number(effectiveTaxRate.toFixed(1)),
    };

    updateWealthManager();
  }

  function updateFinanceManager() {
    const liquidity = Math.max(0, num("fm-liquidity", 50000));
    const ral = Math.max(0, num("fm-ral", 35000));
    const mensilita = parseInt(str("fm-months"), 10) || 13;
    const fixedExpenses = Math.max(0, num("fm-fixed-expenses", 800));
    let purchaseMonth = Math.round(num("fm-purchase-month", 6));

    purchaseMonth = clamp(purchaseMonth, 1, 24);
    q("fm-purchase-month").value = String(purchaseMonth);

    const annualNet = annualNetForFinanceManager(ral);
    const netMonthly = annualNet / mensilita;
    const availableMonthly = netMonthly - fixedExpenses;

    q("fm-base-net").textContent = `Netto (${mensilita} mensilità): ${euro(netMonthly)}/mese`;
    q("fm-base-available").textContent = `Disponibile base: ${euro(availableMonthly)}/mese`;
    q("fm-13").classList.toggle("hidden", mensilita < 13);
    q("fm-14").classList.toggle("hidden", mensilita !== 14);

    if (mensilita >= 13) {
      q("fm-13").textContent = `13ª a dicembre: +${euro(netMonthly)}`;
    }
    if (mensilita === 14) {
      q("fm-14").textContent = `14ª a giugno: +${euro(netMonthly)}`;
    }

    const currentMonth = new Date().getMonth();
    let result;

    if (state.fmType === "house") {
      const firstHome = checked("fm-house-first-home");
      const houseValue = Math.max(0, num("fm-house-value", 250000));
      const mortgageAmount = Math.max(0, num("fm-house-mortgage", 200000));
      const mortgageYears = parseInt(str("fm-house-years"), 10) || 25;
      const mortgageRate = Math.max(0, num("fm-house-rate", 3.5));
      const agencyPct = Math.max(0, num("fm-house-agency", 3));
      const notaryPct = Math.max(0, num("fm-house-notary", 2));

      const condoOn = checked("fm-house-condo-on");
      const condo = Math.max(0, num("fm-house-condo", 100));
      const heatingOn = checked("fm-house-heating-on");
      const heating = Math.max(0, num("fm-house-heating", 150));

      const downPayment = houseValue - mortgageAmount;
      const agencyFee = houseValue * (agencyPct / 100);
      const notaryFee = houseValue * (notaryPct / 100);

      const taxOnDownPayment = firstHome
        ? Math.max(1000, (houseValue - mortgageAmount) * 0.02)
        : Math.max(1000, (houseValue - mortgageAmount) * 0.09);
      const totalTaxes = taxOnDownPayment + 50 + 50;

      const totalUpfront = downPayment + agencyFee + notaryFee + totalTaxes;
      const monthlyMortgage = monthlyPayment(mortgageAmount, mortgageRate, mortgageYears);
      const monthlyHousingCosts = monthlyMortgage + (condoOn ? condo : 0) + (heatingOn ? heating : 0);
      const newAvailableMonthly = availableMonthly - monthlyHousingCosts;

      let liquidityAtPurchase = liquidity;
      for (let m = 0; m < purchaseMonth; m += 1) {
        const monthIndex = (currentMonth + m) % 12;
        const income = monthlyNetWithMensilita(annualNet, mensilita, monthIndex);
        liquidityAtPurchase += income - fixedExpenses;
      }

      const remainingAfterPurchase = liquidityAtPurchase - totalUpfront;

      const projection = [];
      let wallet = liquidity;

      for (let m = 0; m <= 24; m += 1) {
        const monthIndex = (currentMonth + m) % 12;
        let label = m === 0 ? "Oggi" : MONTH_NAMES[monthIndex];

        if (m > 0) {
          const income = monthlyNetWithMensilita(annualNet, mensilita, (currentMonth + m - 1) % 12);
          if (m < purchaseMonth) {
            wallet += income - fixedExpenses;
          } else if (m === purchaseMonth) {
            wallet += income - fixedExpenses;
            wallet -= totalUpfront;
          } else {
            wallet += income - fixedExpenses - monthlyHousingCosts;
          }
        }

        if (monthIndex === 11 && mensilita >= 13) {
          label += " (13ª)";
        }
        if (monthIndex === 5 && mensilita === 14) {
          label += " (14ª)";
        }

        projection.push({
          month: label,
          liquidity: Math.round(wallet),
          purchaseMonth: m === purchaseMonth,
        });
      }

      const debtToIncome = netMonthly > 0 ? (monthlyMortgage / netMonthly) * 100 : 0;
      const affordable = remainingAfterPurchase >= 0 && newAvailableMonthly >= 0;

      result = {
        type: "house",
        liquidityAtPurchase,
        remainingAfterPurchase,
        monthlyCosts: monthlyHousingCosts,
        newAvailableMonthly,
        projection,
        affordable,
        debtToIncome,
        downPayment,
        agencyFee,
        notaryFee,
        totalTaxes,
        totalUpfront,
      };

      q("fm-house-initial-summary").innerHTML = [
        ["Anticipo", downPayment],
        ["Agenzia", agencyFee],
        ["Notaio", notaryFee],
        ["Imposte", totalTaxes],
        ["Totale", totalUpfront],
      ]
        .map(
          ([label, value]) =>
            `<div class="list-item"><span>${label}</span><strong>${euro(value)}</strong></div>`
        )
        .join("");
    } else {
      const carValue = Math.max(0, num("fm-car-value", 25000));
      const downPayment = Math.max(0, num("fm-car-down-payment", 5000));
      const years = parseInt(str("fm-car-years"), 10) || 5;
      const rate = Math.max(0, num("fm-car-rate", 5.5));
      const annualInsurance = Math.max(0, num("fm-car-insurance", 600));
      const maintenance = Math.max(0, num("fm-car-maintenance", 100));

      const loanAmount = carValue - downPayment;
      const monthlyLoan = loanAmount > 0 ? monthlyPayment(loanAmount, rate, years) : 0;
      const monthlyInsurance = annualInsurance / 12;
      const monthlyCarCosts = monthlyLoan + monthlyInsurance + maintenance;
      const newAvailableMonthly = availableMonthly - monthlyCarCosts;

      let liquidityAtPurchase = liquidity;
      for (let m = 0; m < purchaseMonth; m += 1) {
        const monthIndex = (currentMonth + m) % 12;
        const income = monthlyNetWithMensilita(annualNet, mensilita, monthIndex);
        liquidityAtPurchase += income - fixedExpenses;
      }

      const remainingAfterPurchase = liquidityAtPurchase - downPayment;

      const projection = [];
      let wallet = liquidity;

      for (let m = 0; m <= 24; m += 1) {
        const monthIndex = (currentMonth + m) % 12;
        let label = m === 0 ? "Oggi" : MONTH_NAMES[monthIndex];

        if (m > 0) {
          const income = monthlyNetWithMensilita(annualNet, mensilita, (currentMonth + m - 1) % 12);
          if (m < purchaseMonth) {
            wallet += income - fixedExpenses;
          } else if (m === purchaseMonth) {
            wallet += income - fixedExpenses;
            wallet -= downPayment;
          } else {
            wallet += income - fixedExpenses - monthlyCarCosts;
          }
        }

        if (monthIndex === 11 && mensilita >= 13) {
          label += " (13ª)";
        }
        if (monthIndex === 5 && mensilita === 14) {
          label += " (14ª)";
        }

        projection.push({
          month: label,
          liquidity: Math.round(wallet),
          purchaseMonth: m === purchaseMonth,
        });
      }

      const affordable = remainingAfterPurchase >= 0 && newAvailableMonthly >= 0;

      result = {
        type: "car",
        liquidityAtPurchase,
        remainingAfterPurchase,
        monthlyCosts: monthlyCarCosts,
        newAvailableMonthly,
        projection,
        affordable,
      };

      q("fm-house-initial-summary").innerHTML =
        '<div class="list-item"><span>In modalità auto il riepilogo iniziale riguarda l\'anticipo.</span><strong>' +
        euro(downPayment) +
        "</strong></div>";
    }

    q("fm-stat-liquidity-purchase").textContent = euro(result.liquidityAtPurchase);
    q("fm-stat-liquidity-purchase-sub").textContent = `Tra ${purchaseMonth} mesi`;
    q("fm-stat-remaining").textContent = euro(result.remainingAfterPurchase);
    q("fm-stat-monthly-cost-label").textContent =
      state.fmType === "house" ? "Rata + Spese Casa" : "Rata + Spese Auto";
    q("fm-stat-monthly-cost").textContent = euro(result.monthlyCosts);
    q("fm-stat-new-available").textContent = euro(result.newAvailableMonthly);

    const fmAlert = q("fm-alert");
    fmAlert.className = "callout";

    if (result.affordable) {
      fmAlert.classList.add("success");
      if (result.type === "house") {
        fmAlert.innerHTML = `<strong>Acquisto sostenibile.</strong> Avrai ${euro(
          result.remainingAfterPurchase
        )} di liquidità residua e ${euro(result.newAvailableMonthly)}/mese disponibili dopo l'acquisto. Rapporto rata/reddito: ${percent(
          result.debtToIncome,
          1
        )} (max consigliato: 33%).`;
      } else {
        fmAlert.innerHTML = `<strong>Acquisto sostenibile.</strong> Avrai ${euro(
          result.remainingAfterPurchase
        )} di liquidità residua e ${euro(result.newAvailableMonthly)}/mese disponibili dopo l'acquisto.`;
      }
    } else {
      fmAlert.classList.add("danger");
      fmAlert.innerHTML = `<strong>Attenzione:</strong> con i parametri attuali, l'acquisto potrebbe non essere sostenibile. Considera di aumentare l'anticipo, ridurre l'importo del ${
        result.type === "house" ? "mutuo" : "finanziamento"
      }, o posticipare l'acquisto.`;
    }

    const labels = result.projection.map((p) => p.month);
    const values = result.projection.map((p) => p.liquidity);
    const purchasePointValues = result.projection.map((p) => (p.purchaseMonth ? p.liquidity : null));
    const zeroLine = result.projection.map(() => 0);

    setChart("fmProjection", "fm-projection-chart", {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Liquidità",
            data: values,
            borderColor: "#4f46e5",
            backgroundColor: "rgba(79, 70, 229, 0.16)",
            fill: true,
            tension: 0.22,
            pointRadius: 2,
          },
          {
            label: "Acquisto",
            data: purchasePointValues,
            showLine: false,
            pointRadius: 5,
            pointBackgroundColor: "#ef4444",
            pointBorderColor: "#b91c1c",
          },
          {
            label: "Soglia zero",
            data: zeroLine,
            borderColor: "#ef4444",
            borderDash: [6, 4],
            pointRadius: 0,
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2.6,
        plugins: {
          legend: { position: "bottom" },
        },
        scales: {
          y: {
            ticks: { callback: (v) => `€${Math.round(v / 1000)}k` },
          },
        },
      },
    });

    state.latest.financeManager = {
      type: result.type,
      liquidityAtPurchase: Math.round(result.liquidityAtPurchase),
      remainingAfterPurchase: Math.round(result.remainingAfterPurchase),
      monthlyCosts: Math.round(result.monthlyCosts),
      newAvailableMonthly: Math.round(result.newAvailableMonthly),
      affordable: result.affordable,
      debtToIncome: result.debtToIncome ? Number(result.debtToIncome) : null,
    };

    updateWealthManager();
  }

  function updateWealthManager() {
    if (!q("wealth-current-assets")) {
      return;
    }

    const assets = Math.max(0, num("wealth-current-assets", 50000));
    const monthlySavings = Math.max(0, num("wealth-monthly-savings", 700));
    const horizonYears = clamp(Math.round(num("wealth-horizon-years", 10)), 3, 30);
    const emergencyTargetMonths = clamp(Math.round(num("wealth-emergency-target", 6)), 1, 24);
    const extraInvestment = Math.max(0, num("wealth-extra-investment", 0));
    const riskProfile = str("wealth-risk-profile");
    const annualReturnMap = { prudente: 0.03, bilanciato: 0.05, dinamico: 0.07 };
    const annualVolatility = { prudente: 0.04, bilanciato: 0.09, dinamico: 0.14 };

    const annualReturn = annualReturnMap[riskProfile] ?? annualReturnMap.bilanciato;
    const volatility = annualVolatility[riskProfile] ?? annualVolatility.bilanciato;
    const monthlyRate = annualReturn / 12;
    const months = horizonYears * 12;

    q("wealth-horizon-years").value = String(horizonYears);
    q("wealth-emergency-target").value = String(emergencyTargetMonths);

    const projectWithRate = (yearlyRate) => {
      const r = yearlyRate / 12;
      if (r === 0) {
        return assets + extraInvestment + monthlySavings * months;
      }
      return (
        (assets + extraInvestment) * Math.pow(1 + r, months) +
        monthlySavings * ((Math.pow(1 + r, months) - 1) / r)
      );
    };

    const stressRate = Math.max(-0.01, annualReturn - volatility * 0.55);
    const optimisticRate = annualReturn + volatility * 0.45;
    const scenarioStress = projectWithRate(stressRate);
    const scenarioBase = projectWithRate(annualReturn);
    const scenarioOptimistic = projectWithRate(optimisticRate);

    const fmRal = Math.max(0, num("fm-ral", 35000));
    const fmMensilita = parseInt(str("fm-months"), 10) || 13;
    const fixedExpenses = Math.max(0, num("fm-fixed-expenses", 800));
    const purchaseMonth = clamp(Math.round(num("fm-purchase-month", 6)), 1, 24);
    const baseLiquidity = Math.max(0, num("fm-liquidity", 50000));

    const annualNet = annualNetForFinanceManager(fmRal);
    const netMonthly = annualNet / fmMensilita;
    const availableMonthly = netMonthly - fixedExpenses;
    let liquidityAtPurchase = baseLiquidity;
    const currentMonth = new Date().getMonth();
    for (let i = 0; i < purchaseMonth; i += 1) {
      const monthIndex = (currentMonth + i) % 12;
      liquidityAtPurchase += monthlyNetWithMensilita(annualNet, fmMensilita, monthIndex) - fixedExpenses;
    }

    const houseValue = Math.max(0, num("fm-house-value", 250000));
    const houseMortgage = Math.max(0, num("fm-house-mortgage", 200000));
    const houseRate = Math.max(0, num("fm-house-rate", 3.5));
    const houseYears = parseInt(str("fm-house-years"), 10) || 25;
    const houseAgency = Math.max(0, num("fm-house-agency", 3));
    const houseNotary = Math.max(0, num("fm-house-notary", 2));
    const firstHome = checked("fm-house-first-home");
    const houseCondo = checked("fm-house-condo-on") ? Math.max(0, num("fm-house-condo", 100)) : 0;
    const houseHeating = checked("fm-house-heating-on") ? Math.max(0, num("fm-house-heating", 150)) : 0;
    const houseTaxes = (firstHome ? Math.max(1000, (houseValue - houseMortgage) * 0.02) : Math.max(1000, (houseValue - houseMortgage) * 0.09)) + 100;
    const houseUpfront = houseValue - houseMortgage + houseValue * (houseAgency / 100) + houseValue * (houseNotary / 100) + houseTaxes;
    const houseMonthly = monthlyPayment(houseMortgage, houseRate, houseYears) + houseCondo + houseHeating;
    const houseRemaining = liquidityAtPurchase - houseUpfront;
    const houseAfterMonthly = availableMonthly - houseMonthly;
    const houseAffordable = houseRemaining >= 0 && houseAfterMonthly >= 0;

    const carValue = Math.max(0, num("fm-car-value", 25000));
    const carDown = Math.max(0, num("fm-car-down-payment", 5000));
    const carYears = parseInt(str("fm-car-years"), 10) || 5;
    const carRate = Math.max(0, num("fm-car-rate", 5.5));
    const carInsurance = Math.max(0, num("fm-car-insurance", 600));
    const carMaintenance = Math.max(0, num("fm-car-maintenance", 100));
    const carMonthly = monthlyPayment(Math.max(0, carValue - carDown), carRate, carYears) + carInsurance / 12 + carMaintenance;
    const carRemaining = liquidityAtPurchase - carDown;
    const carAfterMonthly = availableMonthly - carMonthly;
    const carAffordable = carRemaining >= 0 && carAfterMonthly >= 0;

    const emergencyTargetValue = fixedExpenses * emergencyTargetMonths;
    const emergencyCoverageMonths = fixedExpenses > 0 ? assets / fixedExpenses : 0;
    const emergencyGap = emergencyTargetValue - assets;

    q("wealth-forecast-total").textContent = euro(scenarioBase);
    q("wealth-forecast-return").textContent = `Rendimento medio annuo stimato: ${percent(annualReturn * 100, 1)}`;
    q("wealth-emergency-coverage").textContent = `${emergencyCoverageMonths.toFixed(1)} mesi`;
    q("wealth-emergency-gap").textContent =
      emergencyGap <= 0
        ? `Copertura completa: +${euro(Math.abs(emergencyGap))}`
        : `Gap da colmare: ${euro(emergencyGap)}`;
    q("wealth-house-impact").textContent = euro(houseRemaining);
    q("wealth-house-impact-note").textContent = houseAffordable
      ? `Sostenibile • disponibile post acquisto ${euro(houseAfterMonthly)}/mese`
      : `Critico • disponibile post acquisto ${euro(houseAfterMonthly)}/mese`;
    q("wealth-car-impact").textContent = euro(carRemaining);
    q("wealth-car-impact-note").textContent = carAffordable
      ? `Sostenibile • disponibile post acquisto ${euro(carAfterMonthly)}/mese`
      : `Critico • disponibile post acquisto ${euro(carAfterMonthly)}/mese`;

    const pensionCost = state.latest.pension?.effectiveCost ?? 0;
    const salaryTaxRate = state.latest.salary?.effectiveTaxRate ?? 0;
    const debtRatio = state.latest.loan?.debtToIncomeRatio ?? null;
    const cases = [
      {
        title: "Fondo emergenza",
        detail:
          emergencyGap <= 0
            ? `Obiettivo raggiunto (${emergencyTargetMonths} mesi di copertura).`
            : `Ti mancano ${euro(emergencyGap)} per raggiungere ${emergencyTargetMonths} mesi.`,
        status: emergencyGap <= 0 ? "positive" : emergencyGap <= emergencyTargetValue * 0.35 ? "warning" : "danger",
      },
      {
        title: "Scenario casa",
        detail: houseAffordable
          ? `Acquisto sostenibile: residuo ${euro(houseRemaining)} e margine mensile ${euro(houseAfterMonthly)}.`
          : `Scenario stress: residuo ${euro(houseRemaining)} e margine mensile ${euro(houseAfterMonthly)}.`,
        status: houseAffordable ? "positive" : houseAfterMonthly >= 0 ? "warning" : "danger",
      },
      {
        title: "Scenario auto",
        detail: carAffordable
          ? `Sostenibile con margine mensile ${euro(carAfterMonthly)}.`
          : `Margine insufficiente (${euro(carAfterMonthly)}/mese).`,
        status: carAffordable ? "positive" : carAfterMonthly >= 0 ? "warning" : "danger",
      },
      {
        title: "Efficienza fiscale lavoro",
        detail: `Aliquota effettiva attuale: ${percent(salaryTaxRate, 1)}.${salaryTaxRate > 34 ? " Valuta ottimizzazioni (benefit/welfare/previdenza)." : ""}`,
        status: salaryTaxRate <= 30 ? "positive" : salaryTaxRate <= 36 ? "warning" : "danger",
      },
      {
        title: "Previdenza complementare",
        detail:
          pensionCost <= 0
            ? "Contribuzione pensione neutra o vantaggiosa."
            : `Costo effettivo netto stimato: ${euro(pensionCost)}/mese.`,
        status: pensionCost <= 70 ? "positive" : pensionCost <= 180 ? "warning" : "danger",
      },
    ];

    if (debtRatio !== null) {
      cases.push({
        title: "Rapporto rata/reddito",
        detail: `Valore corrente: ${percent(debtRatio, 1)} (target consigliato <= 33%).`,
        status: debtRatio <= 33 ? "positive" : debtRatio <= 40 ? "warning" : "danger",
      });
    }

    q("wealth-cases").innerHTML = cases
      .map(
        (item) =>
          `<article class=\"list-item ${item.status}\"><div><strong>${item.title}</strong><br><small>${item.detail}</small></div></article>`
      )
      .join("");

    setChart("wealthScenarios", "wealth-scenarios-chart", {
      type: "bar",
      data: {
        labels: ["Stress", "Base", "Crescita"],
        datasets: [
          {
            label: "Patrimonio stimato",
            data: [Math.round(scenarioStress), Math.round(scenarioBase), Math.round(scenarioOptimistic)],
            backgroundColor: ["#f97316", "#0f766e", "#2563eb"],
            borderRadius: 10,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2.5,
        plugins: {
          legend: { display: false },
        },
        scales: {
          y: {
            ticks: { callback: (v) => `€${Math.round(v / 1000)}k` },
          },
        },
      },
    });

    state.latest.wealth = {
      scenarioStress: Math.round(scenarioStress),
      scenarioBase: Math.round(scenarioBase),
      scenarioOptimistic: Math.round(scenarioOptimistic),
      emergencyGap: Math.round(emergencyGap),
      houseAffordable,
      carAffordable,
    };
  }

  function init() {
    wirePageNavigation();
    wireCalculatorTabs();
    wireManagerTabs();
    wireAuthListeners();
    populateSalaryRegions();
    loadPensionSettings();
    setupListeners();
    updateAuthUi();
    renderGoogleLoginButton();
    hydrateSessionFromToken();

    refreshAllCalculators();
  }

  init();

  return () => {
    if (state.auth.saveTimer) {
      clearTimeout(state.auth.saveTimer);
    }
    Object.values(charts).forEach((chart) => {
      if (chart && typeof chart.destroy === "function") {
        chart.destroy();
      }
    });
  };
}
